import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { endDemo, hasRealSessionBehindDemo } from '../auth/sessionStore';
import { de } from '@fsv/core';
import { useBackend, useSessionState } from '../data/hooks';
import { tx } from '@fsv/data';
import { Icon } from './Icon';
import { useToast } from './Toast';
import { useTour } from '../tour/TourProvider';
import './DemoBand.css';

/**
 * The band that says "this is the demo" on every page of a demo session —
 * the demo is shared as a marketing link, so whoever opens it must see at a
 * glance that nothing leaves the browser. Never rendered in a real session.
 * The persona switcher stays in the profile.
 */
export function DemoBand() {
  const state = useSessionState();
  const backend = useBackend();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const tour = useTour();
  if (state.status !== 'signed-in' || state.session.mode !== 'demo' || backend.kind !== 'demo') return null;

  return (
    <div className="demo-band" role="region" aria-label={tx.app.demoBadge}>
      <p className="demo-band__text">
        <Icon name="alert" size={16} />
        {tx.demo.band}
      </p>
      <div className="demo-band__actions">
        {tour.available && tour.progress?.status !== 'running' && (
          <button type="button" className="demo-band__btn" onClick={tour.progress?.status === 'paused' ? tour.resume : tour.start}>
            <Icon name="flag" size={16} />
            {tour.progress?.status === 'paused' ? tx.tour.resume : tx.tour.label}
          </button>
        )}
        <button
          type="button"
          className="demo-band__btn"
          onClick={async () => {
            if (!window.confirm(tx.demo.resetConfirm)) return;
            await backend.reset?.();
            await qc.resetQueries();
            toast.show(tx.toast.demoReset);
          }}
        >
          <Icon name="refresh" size={16} />
          {tx.demo.reset}
        </button>
        <button
          type="button"
          className="demo-band__btn"
          onClick={async () => {
            const now = await endDemo();
            qc.clear();
            // Back to a real session if there is one ('/' picks its home club), else to the login.
            navigate(now === 'api' ? '/' : '/login', { replace: true });
          }}
        >
          <Icon name="close" size={16} />
          {hasRealSessionBehindDemo() ? de.welcome.back : tx.demo.end}
        </button>
      </div>
    </div>
  );
}
